<template>
  <p v-if="showEstimatedDeliveryDate" class="delivery-estimate">
    <span class="label">
      <template v-if="showCountdown && orderCutOff">
        Order within {{ cutOffCountdown }} to receive
      </template>
      <template v-else>Order now to receive by </template>
    </span>
    <span class="date-range">
      {{ estimatedDeliveryDate }}
      <template v-if="showDateRangeMax"> to </template>
      <span v-if="showDateRangeMax">{{ estimatedDeliveryDateMax }}.</span>
    </span>
  </p>
</template>

<script>
import format from "date-fns/format";
import { mapState, mapActions, mapGetters } from "vuex";
import addDays from "date-fns/addDays";
import getDay from "date-fns/getDay";
import isFriday from "date-fns/isFriday";
import isBefore from "date-fns/isBefore";
import isAfter from "date-fns/isAfter";
import intervalToDuration from "date-fns/intervalToDuration";
import differenceInHours from "date-fns/differenceInHours";
import { checkDeliveryDates } from "@/helpers/edd.js";
import * as Sentry from "@sentry/browser";
import { orderCutOffForNextDay } from "@/config.js";
import { zonedTimeToUtc } from "date-fns-tz";

export default {
  name: "DeliveryEstimatorNoInput",
  props: {
    weight: {
      type: Number,
      default: 0.2,
    },
    showCountdown: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      estimatedDeliveryDate: null,
      estimatedDeliveryDateMax: null,
      now: new Date(),
    };
  },
  computed: {
    ...mapState("app", ["userCountry", "deliveryEstimate"]),
    ...mapGetters("app", ["userPostCode", "currencyCode"]),
    showEstimatedDeliveryDate() {
      return (
        this.estimatedDeliveryDate &&
        this.estimatedDeliveryDateMax &&
        this.userCountry
      );
    },
    showDateRangeMax() {
      return this.estimatedDeliveryDate !== this.estimatedDeliveryDateMax;
    },
    cutOffCountdown() {
      const { hours, minutes, seconds } = this.orderCutOff;

      if (hours > 0) {
        return `${hours} hrs, ${minutes} mins, ${seconds} secs`;
      }

      if (minutes > 0) {
        return `${minutes} mins, ${seconds} secs`;
      }

      return `${seconds} secs`;
    },
    orderCutOff() {
      if (!this.currencyCode) {
        return;
      }

      const rule = orderCutOffForNextDay[this.currencyCode];
      const time = rule.time;

      const todayAtCutOff = new Date();
      todayAtCutOff.setHours(...time.split(":"));

      const cutOffToday = zonedTimeToUtc(todayAtCutOff, rule.timezone);
      const cutOffPassed = isAfter(new Date(), cutOffToday);

      const actualCutOffDate = cutOffPassed
        ? addDays(cutOffToday, 1)
        : cutOffToday;

      const duration = differenceInHours(actualCutOffDate, this.now);

      if (duration >= 4) {
        return false;
      }

      return intervalToDuration({
        start: this.now,
        end: actualCutOffDate,
      });
    },
  },
  mounted() {
    this.getEstimatedDeliveryDate();

    this.tick = setInterval(() => {
      this.now = new Date();
    }, 1000);
  },
  methods: {
    ...mapActions("app", ["setUserPreferences", "getDeliveryEstimate"]),
    async getEstimatedDeliveryDate() {
      await this.getDeliveryEstimate({
        weight: this.weight,
        estimatePostcode: true,
      });

      const { estimated_delivery_date, estimated_delivery_date_max } =
        this.deliveryEstimate;

      if (!estimated_delivery_date && !estimated_delivery_date_max) {
        return;
      }

      const datesAreValid = checkDeliveryDates([
        estimated_delivery_date,
        estimated_delivery_date_max,
      ]);

      if (!datesAreValid) {
        Sentry.addBreadcrumb({
          category: "delivery-estimate",
          message: "Estimated dates",
          data: { estimated_delivery_date, estimated_delivery_date_max },
          level: "info",
        });
        Sentry.captureMessage("EDD dates don't pass validation");
        return;
      }

      this.estimatedDeliveryDate = format(
        new Date(estimated_delivery_date),
        "EEEE do MMMM"
      );

      this.estimatedDeliveryDateMax = format(
        new Date(estimated_delivery_date_max),
        "EEEE do MMMM"
      );

      if (this.userCountry === "GB" && isFriday(this.now)) {
        const rule = orderCutOffForNextDay[this.currencyCode];
        const time = rule.time;

        if (isBefore(this.now, new Date().setHours(...time.split(":")))) {
          const currentDayOfWeek = getDay(this.now);
          const daysUntilSaturday = (6 - currentDayOfWeek + 7) % 7;
          const upcomingSaturday = addDays(this.now, daysUntilSaturday);

          this.estimatedDeliveryDate = format(
            new Date(upcomingSaturday),
            "EEEE do MMMM"
          );
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.delivery-estimate {
  font-size: 15px;
  text-align: left;
}
</style>
